<script setup lang="ts">
import { ChevronRight } from "@lucide/vue";
import { computed } from "vue";
import { RouterLink } from "vue-router";

import type { Master } from "@/entities/master";
import { Avatar } from "@/shared/ui";

/**
 * Один рядок списку майстрів (spec §4.2/§8.1): `Avatar`-плейсхолдер ініціалів
 * (декоративний — `aria-hidden`, бо ПІБ-текст поруч є доступним підписом
 * посилання) + ПІБ (truncate) + chevron. Клік → профіль `/masters/:id`. Без
 * per-row дій (видалення — на профілі, ADR-0013).
 */
const props = defineProps<{ master: Master; targetHash?: string }>();

/**
 * ПІБ у природному порядку «Ім'я По-батькові Прізвище». Той самий рядок іде в
 * `Avatar`: `getInitials` бере перше+останнє слово → ініціали Ім'я+Прізвище
 * (по-батькові посередині не псує), тож display і аватар завжди консистентні.
 */
const fullName = computed(() =>
  [props.master.firstName, props.master.middleName, props.master.lastName]
    .filter(Boolean)
    .join(" "),
);

const to = computed(() => {
  const base = { name: "master-profile", params: { id: props.master.id } };
  return props.targetHash ? { ...base, hash: props.targetHash } : base;
});
</script>

<template>
  <li data-testid="master-row">
    <RouterLink
      :to="to"
      class="flex min-h-11 items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Avatar
        :name="fullName"
        :src="master.photoUrl"
        size="md"
        aria-hidden="true"
      />
      <span
        class="min-w-0 flex-1 truncate text-sm font-medium text-foreground"
        :title="fullName"
      >{{ fullName }}</span>
      <ChevronRight
        class="size-4 shrink-0 text-muted-foreground"
        aria-hidden="true"
      />
    </RouterLink>
  </li>
</template>
